/**
 * Adulting 101's own strings — Puerto Rican Spanish first, English second.
 *
 * Kept apart from lib/i18n.ts (which holds the factory's core strings) so a
 * core update to that file never collides with the course's words. Lesson
 * CONTENT is not here: it carries its own {es, en} pairs in /content.
 */
import { getLocale } from './i18n'
import type { Lang, Text } from '../../shared/classroom'

export function lang(): Lang {
  return getLocale() === 'en' ? 'en' : 'es'
}

/** Pick the current language out of a content Text. */
export function L(t: Text | undefined | null): string {
  if (!t) return ''
  return t[lang()] || t.es || t.en || ''
}

const W = {
  tagline: { es: 'Lo que nadie te enseñó en la escuela.', en: 'What school never taught you.' },
  welcomeTitle: { es: 'Bienvenido a Adulting 101', en: 'Welcome to Adulting 101' },
  welcomeBody: { es: 'Dinero, trabajo, casa, vida. En tu teléfono, en la clase, a tu ritmo.', en: 'Money, work, home, life. On your phone, in class, at your pace.' },
  signIn: { es: 'Entrar', en: 'Sign in' },
  signUp: { es: 'Crear mi cuenta', en: 'Create my account' },
  haveAccount: { es: '¿Ya tienes cuenta?', en: 'Already have an account?' },
  noAccount: { es: '¿Primera vez?', en: 'First time?' },
  yourName: { es: 'Tu nombre (como te llama la clase)', en: 'Your name (what the class calls you)' },
  username: { es: 'Usuario', en: 'Username' },
  usernameHint: { es: 'Letras y números, sin espacios', en: 'Letters and numbers, no spaces' },
  passkeyNote: { es: 'No hay contraseña. Tu cara, tu huella o el PIN de tu teléfono es tu llave.', en: "There's no password. Your face, fingerprint or phone PIN is your key." },
  joiningClass: { es: 'Vas a entrar a la clase', en: "You're joining class" },

  hi: { es: '¡Hola', en: 'Hi' },
  level: { es: 'Nivel', en: 'Level' },
  toNext: { es: 'para', en: 'to' },
  maxLevel: { es: 'Nivel máximo. ¡Fuera del nido!', en: 'Top level. Out of the nest!' },
  liveNow: { es: '¡Clase en vivo ahora!', en: 'Class is live now!' },
  enter: { es: 'Entrar', en: 'Join' },
  joinClass: { es: 'Entrar a una clase', en: 'Join a class' },
  classCode: { es: 'Código de la clase', en: 'Class code' },
  join: { es: 'Entrar', en: 'Join' },
  myClasses: { es: 'Mis clases', en: 'My classes' },
  badges: { es: 'Insignias', en: 'Badges' },
  milestones: { es: 'Del dicho al hecho', en: 'Do it for real' },
  milestonesNote: { es: 'Cosas reales que haces fuera de la clase. Si eres menor de edad, hazlas con tu mamá, papá o un adulto de confianza.', en: 'Real things you do outside class. If you are under 18, do them with a parent or trusted adult.' },
  study: { es: 'Practicar', en: 'Practice' },
  studyNote: { es: 'Practica cualquier lección — las que ya vimos y las que vienen. Preguntas en otro orden cada vez, y nadie te regala la respuesta.', en: "Practice any lesson — done or coming up. Questions in a new order every time, and nobody hands you the answer." },
  taught: { es: 'Vista en clase', en: 'Done in class' },
  upcoming: { es: 'Próxima', en: 'Coming up' },
  practiced: { es: 'preguntas practicadas', en: 'questions practiced' },
  questions: { es: 'preguntas', en: 'questions' },
  minutes: { es: 'min', en: 'min' },

  idk: { es: 'No sé', en: "I don't know" },
  yes: { es: 'Sí', en: 'Yes' },
  no: { es: 'No', en: 'No' },
  send: { es: 'Enviar', en: 'Send' },
  yourGuess: { es: 'Tu respuesta', en: 'Your guess' },
  waitingTeacher: { es: 'Mira al frente — tu maestro está explicando.', en: 'Eyes up front — your teacher is explaining.' },
  waitingNext: { es: 'Espera la próxima pregunta…', en: 'Hang on for the next question…' },
  closed: { es: 'La pregunta se cerró.', en: 'The question is closed.' },
  answerWas: { es: 'La respuesta:', en: 'The answer:' },
  canChange: { es: '¡Anotada! Puedes cambiarla hasta que tu maestro cierre la pregunta.', en: 'Got it! You can change it until your teacher closes the question.' },
  youAnswered: { es: 'Ya contestaste esta.', en: 'You already answered this one.' },
  streak: { es: 'racha', en: 'streak' },
  yourTurn: { es: '¡Te toca!', en: 'Your turn!' },
  explain: { es: 'Explícale a la clase lo que piensas. No hay respuesta mala.', en: "Tell the class what you think. There's no bad answer." },
  reviewForYou: { es: 'Repaso para ti', en: 'A review just for you' },
  reviewNote: { es: 'Tu maestro te envió esto para repasar. Tómate tu tiempo.', en: 'Your teacher sent you this to review. Take your time.' },
  gotIt: { es: '¡Entendido!', en: 'Got it!' },
  lessonOver: { es: '¡Se acabó la lección!', en: 'Lesson complete!' },
  youEarned: { es: 'Ganaste', en: 'You earned' },
  keepStudying: { es: 'Seguir estudiando', en: 'Keep studying' },
  home: { es: 'Inicio', en: 'Home' },
  connecting: { es: 'Conectando…', en: 'Connecting…' },
  reconnecting: { es: 'Reconectando…', en: 'Reconnecting…' },
  ended: { es: 'Esta lección ya terminó. Puedes seguir estudiándola cuando quieras.', en: 'This lesson has ended. You can keep studying it any time.' },
  discuss: { es: 'Conversemos', en: "Let's talk" },
  activity: { es: 'Actividad', en: 'Activity' },
  poll: { es: 'Encuesta', en: 'Poll' },
  homework: { es: 'Tarea', en: 'Homework' },
  objectives: { es: 'Vas a poder…', en: "You'll be able to…" },
  next: { es: 'Siguiente', en: 'Next' },
  prev: { es: 'Anterior', en: 'Back' },
  start: { es: 'Empezar', en: 'Start' },
  finish: { es: 'Terminar', en: 'Finish' },
  practiceDone: { es: '¡Terminaste la práctica!', en: 'Practice complete!' },
  practiceAgain: { es: 'Practicar otra vez', en: 'Practice again' },
  practiceNote: { es: 'Tu primer intento en cada pregunta te da XP.', en: 'Your first try at each question earns XP.' },

  teacher: { es: 'Maestro', en: 'Teacher' },
  classes: { es: 'Clases', en: 'Classes' },
  newClass: { es: 'Nueva clase', en: 'New class' },
  className: { es: 'Nombre de la clase', en: 'Class name' },
  create: { es: 'Crear', en: 'Create' },
  scanToJoin: { es: 'Escanea para entrar', en: 'Scan to join' },
  orGoTo: { es: 'o entra a', en: 'or go to' },
  students: { es: 'Estudiantes', en: 'Students' },
  noStudents: { es: 'Nadie todavía. Enséñales el código QR.', en: 'Nobody yet. Show them the QR code.' },
  startLive: { es: 'Empezar en vivo', en: 'Start live' },
  backToLive: { es: 'Volver a la clase en vivo', en: 'Back to the live class' },
  lessons: { es: 'Lecciones', en: 'Lessons' },
  needsReteach: { es: 'Necesitan repaso', en: 'Need a reteach' },
  reteachWhy: { es: '3 "no sé" seguidos', en: '3 "I don\'t know" in a row' },
  callOn: { es: 'Llamar', en: 'Call on' },
  sendReview: { es: 'Enviar repaso', en: 'Send review' },
  resolved: { es: 'Resuelto', en: 'Resolved' },
  history: { es: 'Historial', en: 'History' },
  presentScreen: { es: 'Pantalla', en: 'Projector' },
  endLesson: { es: 'Terminar lección', en: 'End lesson' },
  confirmEnd: { es: '¿Terminar la lección? Los estudiantes ya no podrán contestar.', en: 'End the lesson? Students will no longer be able to answer.' },
  open: { es: 'Abrir', en: 'Open' },
  close: { es: 'Cerrar', en: 'Close' },
  reveal: { es: 'Revelar', en: 'Reveal' },
  answered: { es: 'contestaron', en: 'answered' },
  here: { es: 'presentes', en: 'here' },
  right: { es: 'Correcto', en: 'Right' },
  close_: { es: 'Cerca', en: 'Close' },
  wrong: { es: 'Incorrecto', en: 'Wrong' },
  none: { es: 'Sin contestar', en: 'No answer' },
  any: { es: 'Cualquiera', en: 'Anyone' },
  random: { es: 'Llamar al azar', en: 'Call at random' },
  nobodyInPool: { es: 'No hay nadie en ese grupo.', en: 'Nobody in that group.' },
  called: { es: 'Llamado', en: 'Called on' },
  dismiss: { es: 'Listo', en: 'Done' },
  teacherNote: { es: 'Nota para el maestro', en: 'Teacher note' },
  xp: { es: 'XP', en: 'XP' },
  rightPct: { es: '% correcto', en: '% right' },
  leaderboard: { es: 'Los que más brillaron', en: 'Top of the class' },
  sound: { es: 'Sonido', en: 'Sound' },
  signOut: { es: 'Salir', en: 'Sign out' },
  account: { es: 'Cuenta', en: 'Account' },
  admin: { es: 'Admin', en: 'Admin' },
  oro: { es: 'XP', en: 'XP' },
  oroShort: { es: 'XP', en: 'XP' },
  xpTotal: { es: 'XP total', en: 'total XP' },
  xpSpend: { es: 'XP para gastar', en: 'XP to spend' },
  connected: { es: 'conectados', en: 'connected' },
  of: { es: 'de', en: 'of' },
  newHere: { es: 'Soy nuevo', en: "I'm new" },
  haveOne: { es: 'Ya tengo cuenta', en: 'I have an account' },
  eyesUp: { es: '¡Ojos al frente!', en: 'Eyes up front!' },
  eyesUpSub: { es: 'Tu maestro está explicando. La próxima pregunta llega aquí.', en: 'Your teacher is explaining. The next question will pop up here.' },
  waitNext: { es: 'Listo. Mira al frente — la próxima llega sola.', en: "Done. Eyes up front — the next one comes by itself." },
  tapNext: { es: 'Toca para seguir', en: 'Tap to keep going' },
  shop: { es: 'La Tienda de Pichi', en: "Pichi's Shop" },
  shopSub: { es: 'Gasta tu XP en sombreros, gafas, colores y fondos. Tu nivel nunca baja.', en: 'Spend your XP on hats, glasses, colors and backgrounds. Your level never drops.' },
  wallet: { es: 'Tienes', en: 'You have' },
  buyFor: { es: 'Comprar por', en: 'Buy for' },
  wear: { es: 'Ponérmelo', en: 'Wear it' },
  takeOff: { es: 'Quitármelo', en: 'Take it off' },
  wearing: { es: 'Puesto', en: 'Wearing' },
  owned: { es: 'Tuyo', en: 'Yours' },
  needMore: { es: 'Te faltan', en: 'You need' },
  goShop: { es: 'Ir a la tienda', en: 'Go to the shop' },
  levels: { es: 'Tus niveles', en: 'Your levels' },
  levelsSub: { es: 'Pichi crece contigo: de huevo a adulto. Ganas XP contestando, practicando y haciendo cosas de verdad. Tu nivel cuenta todo lo que has ganado — gastar en la tienda no lo baja.', en: 'Pichi grows with you: from egg to adult. You earn XP by answering, practicing and doing real things. Your level counts everything you ever earned — spending in the shop never lowers it.' },
  youAreHere: { es: 'Estás aquí', en: "You're here" },
  from: { es: 'desde', en: 'from' },
  howToEarn: { es: 'Cómo ganar', en: 'How to earn' },
  earnRight: { es: 'Respuesta correcta en clase', en: 'Right answer in class' },
  earnTry: { es: 'Intentar (aunque falles)', en: 'Trying (even if wrong)' },
  earnIdk: { es: 'Decir "no sé" con honestidad', en: 'Honestly saying "I don\'t know"' },
  earnPractice: { es: 'Practicar una pregunta (1ra vez)', en: 'Practicing a question (1st time)' },
  earnMilestone: { es: 'Algo de verdad, verificado', en: 'A real-world thing, verified' },
  doneInClass: { es: 'HECHO', en: 'DONE' },
  showTeacher: { es: 'Enséñaselo a tu maestro para ganar', en: 'Show your teacher to earn' },
  verified: { es: 'Verificado', en: 'Verified' },
  joinAClass: { es: 'Entrar a una clase', en: 'Join a class' },
  codeHint: { es: 'Una letra y dos números — mira la pantalla', en: 'One letter and two numbers — look at the screen' },
  feedback: { es: 'Comentarios', en: 'Feedback' },
  feedbackTitle: { es: '¿Qué piensas?', en: 'What do you think?' },
  feedbackSub: { es: 'Algo que te gustó, algo que no, una idea… lo leemos todo.', en: "Something you liked, didn't like, an idea… we read it all." },
  feedbackThanks: { es: '¡Gracias! Lo recibimos.', en: 'Thanks! We got it.' },
  cancel: { es: 'Cancelar', en: 'Cancel' },
  verifyTitle: { es: 'Cosas de verdad', en: 'Real-world milestones' },
  verifySub: { es: 'Marca lo que el estudiante te enseñó. Cada uno le da 250 XP.', en: 'Tick what the student showed you. Each one gives them 250 XP.' },
  grantTitle: { es: 'Dar puntos', en: 'Give points' },
  grantSub: { es: 'Un premio o una corrección. Usa un número negativo para quitar puntos.', en: 'A prize or a correction. Use a negative number to take points away.' },
  grantPoints: { es: 'Puntos', en: 'Points' },
  grantReason: { es: '¿Por qué?', en: 'Why?' },
  grantGive: { es: 'Dar', en: 'Give' },
  grantBadAmount: { es: 'Un número entero, no cero, hasta 10,000.', en: 'A whole number, not zero, up to 10,000.' },
  topics: { es: 'Los temas', en: 'The topics' },
  letsPractice: { es: '¡A practicar!', en: "Let's practice!" },
  question: { es: 'Pregunta', en: 'Question' },
  firstTry: { es: 'al primer intento', en: 'on the first try' },
  hintHere: { es: 'Aquí tienes una pista. ¡Ahora inténtalo!', en: "Here's a hint. Now give it a shot!" },
  higher: { es: 'Más alto ↑', en: 'Higher ↑' },
  lower: { es: 'Más bajo ↓', en: 'Lower ↓' },
  warm: { es: '¡caliente!', en: 'getting warm!' },
  seeHint: { es: 'Ver una pista', en: 'See a hint' },
  firstTryCheer: { es: '¡A la primera!', en: 'First try!' },
  foundIt: { es: '¡La encontraste!', en: 'You found it!' },
  noCallWrong: { es: 'A quien falló no se le llama', en: "Students who got it wrong aren't called on" },
  joinAt: { es: 'Entra a', en: 'Go to' },
  withCode: { es: 'y pon el código', en: 'and enter the code' },
  needBase: { es: 'Primero compra', en: 'First buy' },
  practiceDoneTag: { es: 'Práctica completa', en: 'Practice complete' },
  practiceProgress: { es: 'Práctica', en: 'Practice' },
  legend: { es: 'HECHO = la terminaste en clase. La barra = cuánto has practicado (puedes practicar otra vez cuando quieras).', en: 'DONE = you finished it in class. The bar = how much you have practiced (practice again any time).' },
  quit: { es: 'Salir', en: 'Quit' },
  badgeGot: { es: '¡La tienes!', en: 'You have it!' },
  badgeNotYet: { es: 'Todavía no — ¡tú puedes!', en: 'Not yet — you can do it!' },
  badgesSub: { es: 'Cada insignia tiene 3 niveles: bronce, plata y oro. Cada nivel te da XP. Todas en oro = la Corona de Campeón.', en: "Each badge has 3 tiers: bronze, silver and gold. Every tier pays XP. All gold = the Champion's Crown." },
  bronze: { es: 'Bronce', en: 'Bronze' },
  silver: { es: 'Plata', en: 'Silver' },
  gold: { es: 'Oro', en: 'Gold' },
  badgeMaxed: { es: '¡Oro! Lo lograste todo en esta.', en: 'Gold! You maxed this one out.' },
  championHint: { es: 'Todas las insignias en oro desbloquean la Corona de Campeón — no se vende.', en: "Every badge at gold unlocks the Champion's Crown — it can't be bought." },
  championGot: { es: '¡Campeón! La Corona de Campeón es tuya en la tienda.', en: "Champion! The Champion's Crown is yours in the shop." },
  earnCall: { es: 'Contestar cuando te llaman', en: 'Answering when called on' },
  earnBadge: { es: 'Cada nivel de insignia', en: 'Each badge tier' },
  exclusive: { es: 'Solo con todas las insignias en oro', en: 'Only with every badge at gold' },
  callBonus: { es: '¡Gracias por contestar!', en: 'Thanks for answering!' },

  // Sign in with a PIN (a teacher-made account, and the store reviewer's)
  pinSignIn: { es: 'Entrar con PIN', en: 'Sign in with a PIN' },
  pinSignInHint: { es: 'Si tu maestro te dio un usuario y un PIN.', en: 'If your teacher gave you a username and a PIN.' },
  pin: { es: 'PIN', en: 'PIN' },
  backToPasskey: { es: 'Usar mi llave de acceso', en: 'Use my passkey' },

  guideLink: { es: '¿Primera vez? Mira la guía paso a paso', en: 'First time? See the step-by-step guide' },


  // Teacher lessons: the library, drafting, sharing (Lessons.tsx)
  myLessons: { es: 'Mis lecciones', en: 'My lessons' },
  myLessonsSub: { es: 'Crea lecciones con IA, sube las tuyas, o acepta las que te compartan otros maestros. Cada maestro tiene su propia biblioteca.', en: 'Create lessons with AI, upload your own, or accept ones other teachers share. Every teacher has their own library.' },
  tokens: { es: 'fichas', en: 'tokens' },
  tokensHint: { es: 'Cada lección creada con IA usa 1 ficha. Si falla, te la devolvemos.', en: 'Each AI lesson uses 1 token. If it fails, you get it back.' },
  createWithAi: { es: 'Crear con IA', en: 'Create with AI' },
  createWithAiSub: { es: 'Dinos el tema y te preparamos una lección completa, en español y en inglés, para que la revises.', en: 'Tell us the topic and we draft a full lesson, in Spanish and English, for you to review.' },
  topic: { es: 'Tema', en: 'Topic' },
  topicPh: { es: 'Ej.: Alquilar tu primer apartamento', en: 'E.g. Renting your first apartment' },
  gradeLevel: { es: 'Grado', en: 'Grade' },
  classMinutes: { es: 'Minutos de clase', en: 'Class minutes' },
  notesForAi: { es: 'Algo más (opcional)', en: 'Anything else (optional)' },
  notesPh: { es: 'Ej.: incluye el contrato de arrendamiento y el depósito', en: 'E.g. include the lease and the deposit' },
  draftIt: { es: 'Preparar lección', en: 'Draft the lesson' },
  uploadLesson: { es: 'Subir mi lección', en: 'Upload my lesson' },
  uploadLessonSub: { es: 'PDF, Word (.docx) o texto. Desde Google Docs: Archivo, Descargar, PDF. La convertimos en una lección de Adulting 101 y tú la revisas.', en: 'PDF, Word (.docx) or text. From Google Docs: File, Download, PDF. We turn it into an Adulting 101 lesson and you review it.' },
  chooseFile: { es: 'Escoger archivo', en: 'Choose a file' },
  drafting: { es: 'Pichi está preparando tu lección. Toma uno o dos minutos...', en: 'Pichi is drafting your lesson. It takes a minute or two...' },
  draftReady: { es: '¡Lista para revisar!', en: 'Ready to review!' },
  aiOff: { es: 'Crear con IA todavía no está activado aquí.', en: 'Create with AI is not switched on here yet.' },
  draft: { es: 'Borrador', en: 'Draft' },
  ready: { es: 'Lista', en: 'Ready' },
  review: { es: 'Revisar', en: 'Review' },
  markReady: { es: 'Marcar lista', en: 'Mark ready' },
  backToDraft: { es: 'Volver a borrador', en: 'Back to draft' },
  share: { es: 'Compartir', en: 'Share' },
  shareWith: { es: 'Usuario del maestro', en: "Teacher's username" },
  shared: { es: 'Enviada a', en: 'Sent to' },
  sourceAi: { es: 'IA', en: 'AI' },
  sourceUpload: { es: 'Subida', en: 'Uploaded' },
  sourceShared: { es: 'De', en: 'From' },
  incoming: { es: 'Te compartieron', en: 'Shared with you' },
  accept: { es: 'Aceptar', en: 'Accept' },
  decline: { es: 'No, gracias', en: 'No thanks' },
  noLessonsYet: { es: 'Todavía no tienes lecciones propias.', en: "You don't have any lessons of your own yet." },
  lessonReviewNote: { es: 'Revisa cada paso: las respuestas correctas están marcadas. Los estudiantes no la ven hasta que la marques lista y la añadas a una clase.', en: 'Check every step: the right answers are marked. Students cannot see it until you mark it ready and add it to a class.' },
  correct: { es: 'Correcta', en: 'Correct' },
  teacherNoteLabel: { es: 'Nota para el maestro', en: 'Teacher note' },
  addLessons: { es: 'Añadir lecciones', en: 'Add lessons' },
  classEmpty: { es: 'Esta clase todavía no tiene lecciones. Añade las de la biblioteca o las tuyas.', en: "This class has no lessons yet. Add some from the library or your own." },
  library: { es: 'Biblioteca de Adulting 101', en: 'Adulting 101 library' },
  addAll: { es: 'Añadir todo el módulo', en: 'Add the whole module' },
  inClassTag: { es: 'En la clase', en: 'In class' },
  removeFromClassList: { es: 'Quitar de la clase', en: 'Remove from class' },
  createOrUpload: { es: 'Crear o subir lecciones', en: 'Create or upload lessons' },
  done: { es: 'Listo', en: 'Done' },

  // Your data
  deleteTitle: { es: 'Borrar mi cuenta', en: 'Delete my account' },
  deleteBody: { es: 'Se borran tu cuenta, tus respuestas, tu práctica, tus puntos y lo que compraste. No se puede deshacer.', en: 'Your account, answers, practice, points and shop items are deleted. This cannot be undone.' },
  deleteTeacherBody: { es: 'Primero borra tus clases (cada una guarda el registro de tus estudiantes).', en: 'Delete your classes first (each one holds your students\u2019 records).' },
  typeUsername: { es: 'Escribe tu usuario para confirmar', en: 'Type your username to confirm' },
  deleteForever: { es: 'Borrar para siempre', en: 'Delete forever' },

  // A teacher's class tools
  classTools: { es: 'Datos de la clase', en: 'Class data' },
  exportCsv: { es: 'Exportar registro (CSV)', en: 'Export record (CSV)' },
  addPinStudent: { es: 'Crear estudiante con PIN', en: 'Add a student with a PIN' },
  addPinStudentSub: { es: 'Para quien no tiene teléfono propio o usa una computadora de la escuela. Entra con usuario y PIN.', en: "For a student without their own phone, or on a school computer. They sign in with a username and PIN." },
  studentName: { es: 'Nombre del estudiante', en: "Student's name" },
  pinDigits: { es: 'PIN (6 números)', en: 'PIN (6 digits)' },
  pinCreated: { es: 'Listo. Dale al estudiante su usuario y PIN.', en: "Done. Give the student their username and PIN." },
  deleteClass: { es: 'Borrar la clase', en: 'Delete the class' },
  deleteClassBody: { es: 'Se borran todas las lecciones y respuestas de esta clase. Los estudiantes mantienen su cuenta y su práctica.', en: 'Every lesson and answer in this class is deleted. Students keep their accounts and their practice.' },
  typeCode: { es: 'Escribe el código de la clase para confirmar', en: 'Type the class code to confirm' },
  removeStudent: { es: 'Sacar de la clase', en: 'Remove from class' },
  removeStudentBody: { es: 'Se borran sus respuestas en esta clase. Si le creaste la cuenta con PIN, se borra la cuenta.', en: 'Their answers in this class are deleted. If you created their account with a PIN, the account is deleted.' },
  confirm: { es: 'Confirmar', en: 'Confirm' },
} satisfies Record<string, Text>

export type WordKey = keyof typeof W

export function w(key: WordKey): string {
  return L(W[key])
}
